// Grids & Gantts — the STOCK CHECK column: what is in stock against what the
// work on the grid needs, part by part.
//
// The column names two others (config.js keeps them as `a` and `b`):
//   A  what is AVAILABLE — an External database column, normally one that
//      adds up every matching row (a part's unrestricted stock in every
//      storage location of an SAP MB52 export)
//   B  what is NEEDED — a number (a part line's Quantity)
// and the work types it judges (a part line's "Part"; blank = every row).
//
// ONE STOCK, MANY USERS. The same part is needed by many rows — every Work
// Instruction of every product of every ship set that uses it — and they all
// draw on the same shelf. Judging each row against the whole stock would call
// every one of them safe while the shelf runs out half-way down the list. So
// the check NETS: per part, the rows are taken in the order the work is due
// (earliest Due date first, then by key), each takes what it needs from what
// is left, and a row is SHORT by whatever the shelf can no longer give it.
// Work that is Done needs nothing any more and is left out.
//
//   part 8300260: 40 in stock · A needs 33 (due 16 Oct) → 7 left · B needs 33
//   (due 18 Oct) → short 26 · C needs 33 (due 20 Oct) → short 33
//
// A row the check does not judge (a Work Instruction on a check of part lines)
// shows how many judged rows under it are short — every level of the tree
// adds up what is below it, the way a structure's counts do.
//
// MADE HERE, NOT TAKEN FROM STOCK. A part a work item on the grid MAKES (its
// Part Number — a trim cut from a bigger moulding, a sub-assembly listed
// without its assembly flag) is not drawn from the shelf: its row reads
// "Made" (`made` = the work item that makes it) and takes no stock from
// anybody. The page decides which parts those are (the column may judge them
// anyway — judgeMade).
//
// Pure and import-free, like every file a client fork's suite may copy.

const keyCmp = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
const round = (n) => Math.round(n * 1000) / 1000;

// entries: [{ key, owner, part, need, avail, due, done, pending, error }]
//   key     the row's key (a work item's key, a line's key)
//   owner   for a line: the work item it belongs to (whose total it adds to)
//   part    what is drawn from the shelf — the value A was looked up by
//   need    B's figure (blank or 0 = needs nothing)
//   avail   A's figure (no row in the source = 0)
//   pending A is still being asked for; error: A could not be read
//   made    the key of a work item on the grid that makes this part — not netted
// Answers:
//   byKey   key → { part, need, avail, before, left, short, pending, error, users, total }
//           before = what earlier rows took, left = what is left after this
//           row, short = what this row cannot get, users/total = how many rows
//           need the part and how much they need in all
//   own     key → 1 for every short row
//   under   work item key → how many of its own lines are short
export function netStock(entries) {
  const byPart = new Map();
  const byKey = new Map();
  (entries || []).forEach((e) => {
    if (!e || e.done || !e.part) return;
    if (e.made) { byKey.set(e.key, { part: e.part, need: round(Number(e.need) || 0), made: e.made, short: 0 }); return; }
    const p = String(e.part).trim().toLowerCase();
    if (!byPart.has(p)) byPart.set(p, []);
    byPart.get(p).push(e);
  });
  const own = new Map();
  const under = new Map();
  byPart.forEach((list) => {
    list.sort((x, y) => {
      const dx = x.due || ''; const dy = y.due || '';
      if (dx !== dy) { if (!dx) return 1; if (!dy) return -1; return dx < dy ? -1 : 1; }
      return keyCmp(x.key, y.key);
    });
    const pending = list.some((e) => e.pending);
    const error = (list.find((e) => e.error) || {}).error || null;
    const avail = list.reduce((m, e) => (Number.isFinite(e.avail) ? Math.max(m, e.avail) : m), 0);
    const total = round(list.reduce((s, e) => s + (Number(e.need) > 0 ? Number(e.need) : 0), 0));
    let taken = 0;
    list.forEach((e) => {
      const need = Number(e.need) > 0 ? Number(e.need) : 0;
      const before = round(taken);
      taken += need;
      const short = pending || error ? 0 : round(Math.min(need, Math.max(0, taken - avail)));
      byKey.set(e.key, {
        part: e.part, need: round(need), avail: round(avail), before, left: round(avail - taken), short,
        pending, error, users: list.length, total,
      });
      if (short > 0) {
        own.set(e.key, 1);
        if (e.owner) under.set(e.owner, (under.get(e.owner) || 0) + 1);
      }
    });
  });
  return { byKey, own, under };
}

// How many short rows sit at and under a row: its own (a judged row), and for
// every distinct work item in its branch that work item's own and its lines'.
export function risksUnder(net, rowKey, branchKeys) {
  if (!net) return 0;
  let n = rowKey && net.own.has(rowKey) && !(branchKeys && branchKeys.has(rowKey)) ? 1 : 0;
  if (branchKeys) branchKeys.forEach((k) => { n += (net.own.get(k) || 0) + (net.under.get(k) || 0); });
  return n;
}

// The short rows under a row, worst first — what the hover card lists.
export function shortUnder(net, rowKey, branchKeys, linesOfKey) {
  if (!net) return [];
  const out = [];
  const add = (k) => { const r = net.byKey.get(k); if (r && r.short > 0) out.push({ key: k, ...r }); };
  if (rowKey && !(branchKeys && branchKeys.has(rowKey))) add(rowKey);
  if (branchKeys) branchKeys.forEach((k) => { add(k); (linesOfKey ? linesOfKey(k) : []).forEach((l) => add(l.key)); });
  return out.sort((a, b) => b.short - a.short || keyCmp(a.part, b.part));
}
